/**
 * Refresh bus — shared channel so anything (idle modal, Update button,
 * transaction-gate, future scheduler) can signal "refresh tier-1 now"
 * and any data-fetching hook can subscribe.
 *
 * Deliberately tiny: a monotonically-increasing counter + dismissed
 * flag. Consumers `useEffect` on the counter to re-fetch; the bus has
 * no opinion on HOW the refresh happens.
 *
 * Not persisted — this is transient session state. The token only
 * matters within a single page session.
 */
import { create } from "zustand";

interface RefreshBusState {
  /**
   * Increments every time someone calls `requestT1Refresh`. Data
   * hooks that want to respond subscribe to this via useEffect.
   * Using a counter rather than a timestamp because timestamps can
   * collide (two calls in the same ms both read as the same Date).
   */
  t1Token: number;
  /**
   * Set to true when the user explicitly dismissed the idle modal
   * without refreshing. The hero FreshnessDot surfaces a warning
   * tone while this is true; it clears the moment a real refresh
   * lands (caller bumps `t1Token` on success).
   */
  staleAcknowledged: boolean;

  /** Request a tier-1 refresh. Any subscriber re-fetches on token change. */
  requestT1Refresh: () => void;
  /** Mark the hero as "user knows it's stale". Visual hint only. */
  markStaleAcknowledged: () => void;
  /** Clear the ack flag — call when a real refresh has landed. */
  clearStaleAcknowledged: () => void;
}

export const useRefreshBus = create<RefreshBusState>()((set) => ({
  t1Token: 0,
  staleAcknowledged: false,
  requestT1Refresh: () =>
    set((s) => ({
      t1Token: s.t1Token + 1,
      // A fresh refresh request implicitly clears any prior ack —
      // the user's choice is visible in the resulting state, so the
      // warning indicator no longer adds information.
      staleAcknowledged: false
    })),
  markStaleAcknowledged: () => set({ staleAcknowledged: true }),
  clearStaleAcknowledged: () => set({ staleAcknowledged: false })
}));
